import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";
import { AuthContext } from "../../Context/AuthContext";

const iStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none" };
const lStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 5, display: "block" };
const fStyle = { marginBottom: 16 };

const Field = ({ label, icon, error, children }) => (
  <div style={fStyle}>
    <label style={{ ...lStyle, color: error ? "#C53030" : "#2D3748" }}>
      {icon && <i className={`mdi ${icon}`} style={{ color: error ? "#C53030" : "#43A047", marginRight: 5 }} />}
      {label}
    </label>
    {children}
    {error && <div style={{ fontSize: 11, color: "#C53030", marginTop: 4 }}>{error}</div>}
  </div>
);

const Input = ({ error, ...props }) => (
  <input
    style={{ ...iStyle, borderColor: error ? "#FC8181" : "#E2E8F0" }}
    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
    onBlur={(e) => (e.target.style.borderColor = error ? "#FC8181" : "#E2E8F0")}
    {...props}
  />
);

const Select = ({ error, children, ...props }) => (
  <select
    style={{ ...iStyle, cursor: "pointer", borderColor: error ? "#FC8181" : "#E2E8F0" }}
    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
    onBlur={(e) => (e.target.style.borderColor = error ? "#FC8181" : "#E2E8F0")}
    {...props}
  >
    {children}
  </select>
);

const Section = ({ icon, title, children, cols = 2 }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #EDF2F7" }}>
      <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
        <i className={`mdi ${icon}`} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#2D3748" }}>{title}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: "0 20px" }}>
      {children}
    </div>
  </div>
);

const EditShg = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const [formData, setFormData] = useState({
    name: "", code: "", vfcId: "", type: "", location: "", address: "",
    village: "", panchayath: "", thaluk: "", pinCode: "",
    dateOfEstablishment: "", bankName: "", branch: "", accountNumber: "", ifscCode: "",
  });
  const [errors, setErrors]       = useState({});
  const [vfcOptions, setVfcOptions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /* Fetch SHG data */
  useEffect(() => {
    const fetchSHG = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.get(`${Api}/api/shgs/${id}`, config);
        const shg = res.data.shg || {};
        setFormData({
          ...shg,
          vfcId: shg.vfcId?._id || shg.vfcId || "",
          dateOfEstablishment: shg.dateOfEstablishment
            ? new Date(shg.dateOfEstablishment).toISOString().split("T")[0]
            : "",
        });
      } catch (e) {
        console.error("Error fetching SHG data", e);
        toast.error("Failed to fetch SHG data.");
      } finally {
        setLoading(false);
      }
    };
    fetchSHG();
  }, [id, Api]);

  /* Fetch VFCs when jfmcType is available */
  useEffect(() => {
    if (!selectedJfmcType) return;
    const fetchVfcs = async () => {
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId: selectedJfmcType._id }, config);
        setVfcOptions(res.data.vfcs || []);
      } catch (e) {
        console.error("Error fetching VFCs", e);
      }
    };
    fetchVfcs();
  }, [selectedJfmcType, Api]);

  const set = (field) => (e) => {
    setFormData((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  };

  const validate = () => {
    const e = {};
    ["name","vfcId","type","location","address","village","panchayath","thaluk","pinCode","dateOfEstablishment","bankName","branch","accountNumber","ifscCode"]
      .forEach((f) => { if (!formData[f]) e[f] = "This field is required"; });
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please fill all required fields"); return; }
    setSubmitting(true);
    try {
      const config = Authorization();
      await axios.put(`${Api}/api/shgs/${id}`, formData, config);
      toast.success("SHG updated successfully");
      navigate("/SHG/manage");
    } catch (e) {
      console.error("Error updating SHG", e);
      toast.error(e.response?.data?.message || "Failed to update SHG");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Edit SHG</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>SHG</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Edit Registration</span>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {/* Card header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
            <i className="mdi mdi-account-group" />
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>SHG Edit Form</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Update the Self Help Group details</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
          {/* Basic Info */}
          <Section icon="mdi-information-outline" title="Basic Information">
            <Field label="SHG Name" icon="mdi-account-group" error={errors.name}>
              <Input value={formData.name} onChange={set("name")} placeholder="Enter SHG name" error={errors.name} />
            </Field>
            <Field label="SHG Code" icon="mdi-identifier" error={errors.code}>
              <Input value={formData.code || ""} onChange={set("code")} placeholder="Enter SHG code" error={errors.code} />
            </Field>
            <Field label="JFMC" icon="mdi-home-group" error={errors.vfcId}>
              <Select value={formData.vfcId} onChange={set("vfcId")} error={errors.vfcId}>
                <option value="">— Select JFMC —</option>
                {vfcOptions.sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((v) => (
                  <option key={v._id} value={v._id}>{v.vfcName}</option>
                ))}
              </Select>
            </Field>
            <Field label="SHG Type" icon="mdi-tag-outline" error={errors.type}>
              <Input value={formData.type} onChange={set("type")} placeholder="e.g. Women SHG" error={errors.type} />
            </Field>
            <Field label="Date of Establishment" icon="mdi-calendar-outline" error={errors.dateOfEstablishment}>
              <Input type="date" value={formData.dateOfEstablishment} onChange={set("dateOfEstablishment")} error={errors.dateOfEstablishment} />
            </Field>
          </Section>

          {/* Location */}
          <Section icon="mdi-map-marker-outline" title="Location Details">
            <Field label="Location" icon="mdi-map-marker" error={errors.location}>
              <Input value={formData.location} onChange={set("location")} placeholder="Enter location" error={errors.location} />
            </Field>
            <Field label="Address" icon="mdi-home-outline" error={errors.address}>
              <Input value={formData.address} onChange={set("address")} placeholder="Enter address" error={errors.address} />
            </Field>
            <Field label="Village" icon="mdi-village" error={errors.village}>
              <Input value={formData.village} onChange={set("village")} placeholder="Enter village" error={errors.village} />
            </Field>
            <Field label="Panchayath" icon="mdi-office-building" error={errors.panchayath}>
              <Input value={formData.panchayath} onChange={set("panchayath")} placeholder="Enter panchayath" error={errors.panchayath} />
            </Field>
            <Field label="Thaluk" icon="mdi-map" error={errors.thaluk}>
              <Input value={formData.thaluk} onChange={set("thaluk")} placeholder="Enter thaluk" error={errors.thaluk} />
            </Field>
            <Field label="Pin Code" icon="mdi-pin-outline" error={errors.pinCode}>
              <Input value={formData.pinCode} onChange={set("pinCode")} placeholder="Enter pin code" error={errors.pinCode} />
            </Field>
          </Section>

          {/* Bank Details */}
          <Section icon="mdi-bank-outline" title="Bank Details">
            <Field label="Bank Name" icon="mdi-bank" error={errors.bankName}>
              <Input value={formData.bankName} onChange={set("bankName")} placeholder="Enter bank name" error={errors.bankName} />
            </Field>
            <Field label="Branch" icon="mdi-source-branch" error={errors.branch}>
              <Input value={formData.branch} onChange={set("branch")} placeholder="Enter branch" error={errors.branch} />
            </Field>
            <Field label="Account Number" icon="mdi-card-account-details-outline" error={errors.accountNumber}>
              <Input value={formData.accountNumber} onChange={set("accountNumber")} placeholder="Enter account number" error={errors.accountNumber} />
            </Field>
            <Field label="IFSC Code" icon="mdi-identifier" error={errors.ifscCode}>
              <Input value={formData.ifscCode} onChange={set("ifscCode")} placeholder="Enter IFSC code" error={errors.ifscCode} />
            </Field>
          </Section>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 10, paddingTop: 8, borderTop: "1px solid #EDF2F7" }}>
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: "9px 22px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(46,125,50,0.3)", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? <><i className="mdi mdi-loading mdi-spin" /> Saving…</> : <><i className="mdi mdi-content-save-outline" /> Update SHG</>}
            </button>
            <button
              type="button"
              onClick={() => navigate("/SHG/manage")}
              style={{ padding: "9px 18px", background: "#F7FAFC", color: "#4A5568", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditShg;
